import styled from 'styled-components'
import { SPACING, BORDER_RADIUS, FONT_WEIGHT } from '../../styles/theme'

// ─── Section header (e.g. "SURVEYS · 4") ─────────────────────────────────────

export const SectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${SPACING.xs};
  margin: 0 0 ${SPACING.sm} 0;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 11px;
  font-weight: ${FONT_WEIGHT.semibold};
`

export const SectionHeaderTitle = styled.span`
  color: var(--color-text-secondary);
`

export const SectionHeaderCount = styled.span`
  color: var(--color-text-tertiary);
  font-weight: ${FONT_WEIGHT.medium};

  &::before {
    content: '·';
    margin-right: 4px;
  }
`

// ─── Dense research rows (surveys & interviews) ──────────────────────────────

const ROW_ACCENT_LIVE = '#22c55e'

export const RowContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${SPACING.xl};

  /* Hairline dividers between rows, no outer border. */
  > * + * {
    border-top: 1px solid var(--color-border);
  }
`

export const ResearchRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${SPACING.md};
  padding: ${SPACING.md} ${SPACING.sm} ${SPACING.md} ${SPACING.lg};
  min-height: 72px;
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover {
    background: var(--color-bg-hover, rgba(255, 255, 255, 0.03));
  }
`

export const RowAccent = styled.div<{ $live: boolean }>`
  position: absolute;
  left: 0;
  top: 8px;
  bottom: 8px;
  width: 3px;
  border-radius: 2px;
  background: ${({ $live }) => ($live ? ROW_ACCENT_LIVE : 'transparent')};
`

export const RowIconBadge = styled.span<{ $live?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${BORDER_RADIUS.md};
  background: ${({ $live }) => ($live ? 'var(--color-primary-bg)' : 'rgba(127, 127, 127, 0.15)')};
  color: ${({ $live }) => ($live ? 'var(--color-primary)' : 'var(--color-text-secondary)')};
  flex-shrink: 0;
`

export const RowMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const RowTitleLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${SPACING.xs};
  min-width: 0;
`

export const RowTitle = styled.span`
  font-size: 15px;
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

export const RowTypeLabel = styled.span`
  font-size: 10px;
  font-weight: ${FONT_WEIGHT.semibold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-tertiary);
  flex-shrink: 0;
`

export const RowStatusLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--color-text-tertiary);
  flex-wrap: wrap;
`

export const RowStatusChip = styled.span<{ $status: 'live' | 'closed' }>`
  font-size: 12px;
  font-weight: ${FONT_WEIGHT.semibold};
  color: ${({ $status }) => ($status === 'live' ? ROW_ACCENT_LIVE : 'var(--color-text-tertiary)')};
`

export const RowStatusItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--color-text-tertiary);
  white-space: nowrap;
`

export const RowStatusSep = styled.span`
  color: var(--color-text-tertiary);
  opacity: 0.5;

  &::before {
    content: '·';
  }
`

export const RowMetric = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  min-width: 70px;
  flex-shrink: 0;
`

export const RowMetricNumber = styled.span`
  font-size: 22px;
  font-weight: ${FONT_WEIGHT.bold};
  color: var(--color-text-primary);
  line-height: 1;
  font-variant-numeric: tabular-nums;
`

export const RowMetricLabel = styled.span`
  font-size: 11px;
  color: var(--color-text-tertiary);
  margin-top: 2px;
`

export const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs};
  flex-shrink: 0;
`

const rowActionButtonBase = `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 8px;
  background: transparent;
  border: 1px solid var(--color-border-dark);
  color: var(--color-text-primary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition:
    background 0.12s ease,
    border-color 0.12s ease,
    color 0.12s ease;

  &:hover {
    background: var(--color-bg-hover);
  }
`

export const RowActionButton = styled.button`
  ${rowActionButtonBase}
`

export const RowRecordDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.15);
`

export const RowMoreButton = styled.button`
  ${rowActionButtonBase}
  width: 34px;
  padding: 0;
  justify-content: center;
  color: var(--color-text-secondary);
`
